import React, { FunctionComponent } from 'react';
import { FormikValues } from 'formik';
import SimpleChargeForm from './SimpleChargeForm';
import { simpleRateListToScheduleList } from './simpleChargeUtils';
import { sanitiseChargeBody, useAddChargesToPlanV2 } from '../planApi';
import { useListData } from 'list-data/ListDataContext';
import { ChargeTypesConfig } from 'list-data/charge-types/chargeTypesConfigUtils';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Charge, Plan, Provider, RateSchedule } from '../../../api/openapi/rating-config';

interface AddSimpleChargeProps {
  close: () => void;
  plan: Plan | undefined;
  provider: Provider;
}

const AddSimpleCharge: FunctionComponent<AddSimpleChargeProps> = ({ close, plan, provider }) => {
  const { fbau635BillingCategoryForInvoicePresentation, cfd872SteppedTariffs } = useFlags();

  const [chargeTypesList] = useListData(['CHARGE_TYPES']);
  const chargeTypesConfig: ChargeTypesConfig = chargeTypesList?.data as ChargeTypesConfig;

  const { mutateAsync, isLoading, isError: errorAddingCharge, error } = useAddChargesToPlanV2();

  const addSimpleCharge =
    () =>
    async ({
      chargeType,
      chargeReference,
      chargeBasis,
      chargeName,
      chargeTags,
      chargeCalculation,
      aggregationMethod,
      billingDescriptor, // Remove with fbau635BillingCategoryForInvoicePresentation
      billingCategory,
      flowDirection,
      powerFactor,
      topPeriods,
      sumFunction,
      adjustmentToUse,
      rates,
      schedules,
      lookbackMonths,
      intervalSize,
      minimumChargeable,
      minimumThresholdMethod,
      historicalMeasure,
      usageChargeFilter,
      readingQuality,
      isTaxed,
    }: FormikValues) => {
      const adjustmentToUseProps =
        typeof adjustmentToUse == 'string' ? { adjustmentToUse: JSON.parse(adjustmentToUse) } : {};

      const displayProps = fbau635BillingCategoryForInvoicePresentation
        ? {
            billingDescriptor,
            display: { category: billingCategory, descriptor: billingDescriptor },
          }
        : { billingDescriptor };

      let schedulesToAdd: Array<RateSchedule>;
      if (cfd872SteppedTariffs) {
        schedulesToAdd = schedules.map((schedule: any) => ({
          ...schedule,
          rates: schedule.rates?.map((rate: any) => ({
            ...rate,
            applicableTimePeriods: rate.applicableTimePeriods?.map((applicableTimePeriod: any) => ({
              qualifierId: applicableTimePeriod.id,
              name: applicableTimePeriod.name,
            })),
            rateQualifiers: rate.rateQualifiers?.map((rateQualifier: any) => ({
              qualifierId: rateQualifier.id,
              name: rateQualifier.name,
            })),
          })),
        }));
      } else {
        schedulesToAdd = simpleRateListToScheduleList(rates);
      }

      const newCharge: Charge = {
        chargeBasis,
        chargeType,
        chargeReference,
        chargeName,
        chargeTags: [chargeTags],
        chargeCalculation,
        aggregationMethod,
        ...displayProps,
        flowDirection: flowDirection,
        powerFactor,
        topPeriods,
        sumFunction,
        ...adjustmentToUseProps,
        schedules: schedulesToAdd,
        lookbackMonths,
        intervalSize,
        minimumChargeable,
        minimumThresholdMethod: minimumThresholdMethod == '' ? undefined : minimumThresholdMethod,
        historicalMeasure,
        usageChargeFilter,
        readingQuality,
        isTaxed,
      };

      const result = await mutateAsync({
        planId: plan?.id,
        charges: [sanitiseChargeBody(newCharge, chargeTypesConfig)],
      } as any);

      if (result) {
        close();
      }
    };

  return (
    <div data-testid="add-simple-charge-form">
      <h2 className="apl-h2 apl-mt-none">Add a charge</h2>
      <SimpleChargeForm
        close={close}
        formAction={addSimpleCharge}
        isLoading={isLoading}
        isChargeError={errorAddingCharge}
        qualifierErrorMessage={(error as Error)?.message ?? ''}
        plan={plan}
        provider={provider}
      />
    </div>
  );
};

export default AddSimpleCharge;
